import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";

import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cleanerApi, setCleanerToken, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function CleanerLoginPage() {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const L = (en, es) => (locale === "es" ? es : en);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await cleanerApi.post("/cleaners/login", { email, password });
      const data = unwrap(r) || {};
      if (!data.access_token) throw new Error("no token");
      setCleanerToken(data.access_token);
      if (data.user?.must_change_password) {
        navigate("/cleaner/change-password");
      } else {
        navigate("/cleaner/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || L("Invalid email or password", "Email o contraseña inválidos"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0C1A1A] grid place-items-center px-4" data-testid="cleaner-login-page">
      <SEO title={L("Cleaner login — Ever Clean Home", "Ingreso de limpiadores — Ever Clean Home")} noindex />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="w-14 h-14 rounded-xl bg-white grid place-items-center p-2"><img src="/brand/logo-everclean-icon-192.png" alt="Ever Clean Home" className="w-full h-full object-contain" /></span>
          <span className="font-display font-extrabold text-xl text-white">Ever Clean Home</span>
        </Link>
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <h1 className="font-display text-2xl font-extrabold tracking-tighter">{L("Cleaner login", "Ingreso de limpiadores")}</h1>
          <p className="text-sm text-[#4B5563] mt-1">{L("Welcome back. Sign in to manage your profile.", "Bienvenido de nuevo. Ingresa para gestionar tu perfil.")}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-sm">{L("Email", "Email")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl mt-1" required data-testid="cleaner-login-email" />
            </div>
            <div>
              <Label className="text-sm">{L("Password", "Contraseña")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl mt-1" required data-testid="cleaner-login-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold" data-testid="cleaner-login-submit">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-4 h-4 mr-2" /> {L("Sign in", "Ingresar")}</>}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-[#4B5563]">
            {L("Not on the team yet?", "¿Todavía no eres parte del equipo?")}{" "}
            <Link to="/work-with-us" className="text-[#0EA5A4] font-semibold hover:underline" data-testid="cleaner-login-apply-link">
              {L("Apply here", "Postúlate aquí")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
